import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { IS_AUTHENTICATED_URL } from "../features/UrlHelper";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLektor, setIsLektor] = useState(false);
  const [userRole, setUserRole] = useState("user");
  const [authLoading, setAuthLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const [userId, setUserId] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userCredits, setUserCredits] = useState(0);

  // Global Auth Modals State
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [forgotModalOpen, setForgotModalOpen] = useState(false);

  const openLoginModal = () => {
    setRegisterModalOpen(false);
    setForgotModalOpen(false);
    setLoginModalOpen(true);
  };

  const openRegisterModal = () => {
    setLoginModalOpen(false);
    setForgotModalOpen(false);
    setRegisterModalOpen(true);
  };

  const openForgotModal = () => {
    setLoginModalOpen(false);
    setRegisterModalOpen(false);
    setForgotModalOpen(true);
  };

  const closeAuthModals = () => {
    setLoginModalOpen(false);
    setRegisterModalOpen(false);
    setForgotModalOpen(false);
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const login = (admin = false, email = null, id = null, credits = 0, fName = "", lName = "", role = "user") => {
    setIsLoggedIn(true);
    setIsAdmin(admin);
    setIsLektor(admin || role === "lektor");
    setUserRole(role);
    setUserEmail(email);
    setUserId(id);
    setUserCredits(parseInt(credits, 10) || 0);
    setFirstName(fName || "");
    setLastName(lName || "");
    closeAuthModals();
    // Refresh to ensure all synced
    checkAuthStatus();
  };

  const logout = () => {
    setIsLoggedIn(false);
    setIsAdmin(false);
    setIsLektor(false);
    setUserRole("user");
    setUserEmail(null);
    setUserId(null);
    setFirstName("");
    setLastName("");
    setUserCredits(0);
    window.localStorage.removeItem("authToken");
    try {
      window.localStorage.removeItem("_ks_client_state");
      window.localStorage.removeItem("_ks_client_pref");
      window.localStorage.removeItem("_ks_client_ref");
    } catch (_) { }
  };

  async function refreshCredits() {
    const token = window.localStorage.getItem("authToken");
    if (!token) return;
    try {
      const response = await axios.get(IS_AUTHENTICATED_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data) {
        setUserCredits(parseInt(response.data.credits, 10) || 0);
        if (response.data.firstName) setFirstName(response.data.firstName);
        if (response.data.lastName) setLastName(response.data.lastName);
        if (response.data.role) setUserRole(response.data.role);
        setIsLektor(response.data.isLektor || response.data.admin || response.data.role === "lektor");
      }
    } catch (e) {
      console.error("Chyba pri aktualizácii kreditov:", e);
    }
  }

  async function checkAuthStatus() {
    const token = window.localStorage.getItem("authToken");
    if (!token) {
      setIsLoggedIn(false);
      setIsAdmin(false);
      setIsLektor(false);
      setUserRole("user");
      setUserEmail(null);
      setUserId(null);
      setFirstName("");
      setLastName("");
      setUserCredits(0);
      setAuthLoading(false);
      return;
    }

    await axios
      .get(IS_AUTHENTICATED_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setIsLoggedIn(true);
        setIsAdmin(response.data.admin);
        setIsLektor(response.data.isLektor || response.data.admin || response.data.role === "lektor");
        setUserRole(response.data.role || (response.data.admin ? "admin" : "user"));
        setUserEmail(response.data.email ?? null);
        setUserId(response.data.id ?? null);
        setFirstName(response.data.firstName ?? "");
        setLastName(response.data.lastName ?? "");
        setUserCredits(parseInt(response.data.credits, 10) || 0);
      })
      .catch(() => {
        setIsLoggedIn(false);
        setIsAdmin(false);
        setIsLektor(false);
        setUserRole("user");
        setUserEmail(null);
        setUserId(null);
        setFirstName("");
        setLastName("");
        setUserCredits(0);
      })
      .finally(() => {
        setAuthLoading(false);
      });
  }

  const userName = [firstName, lastName].filter(Boolean).join(" ");

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isAdmin,
        isLektor,
        userRole,
        authLoading,
        userEmail,
        userId,
        userName,
        firstName,
        lastName,
        userCredits,
        refreshCredits,
        checkAuthStatus,
        login,
        logout,
        loginModalOpen,
        registerModalOpen,
        forgotModalOpen,
        setLoginModalOpen,
        setRegisterModalOpen,
        setForgotModalOpen,
        openLoginModal,
        openRegisterModal,
        openForgotModal,
        closeAuthModals,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
